import { Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type FieldErrorDto, useSubmitQualificationSchemeEndNoticeMutation } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { DoffinNoAnswerAlert } from "./DoffinNoAnswerAlert";
import { DoffinRefusalAlert, doffinRefusal } from "./DoffinRefusalAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "./doffinNoAnswer";

interface Props {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const EndQualificationSchemeModal = ({ eventId, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const [refusal, setRefusal] = useState<FieldErrorDto[] | null>(null);
  const [noAnswer, setNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const [submit, { isLoading }] = useSubmitQualificationSchemeEndNoticeMutation();

  const send = async () => {
    setRefusal(null);
    setNoAnswer(null);
    try {
      await submit({ sourcingEventId: eventId }).unwrap();
      toast({
        title: t("The ending is sent to Doffin. The system ends when Doffin has published it."),
        status: "success",
        position: "top-right",
      });
      onClose();
    } catch (e) {
      const breaches = doffinRefusal(e);
      const unanswered = doffinNoAnswer(e);
      if (breaches) setRefusal(breaches);
      else if (unanswered) setNoAnswer(unanswered);
      else trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">{t("End the qualification system")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing="4">
            <Text>
              {t(
                "The ending is announced on Doffin with a contract award notice that refers to the system's notice (forsyningsforskriften § 13-1 (4)).",
              )}
            </Text>
            <Text>
              {t(
                "Once Doffin has published it, the system takes no more applications and no new competition can be started from it. Competitions already started go on as before.",
              )}
            </Text>
            {refusal && <DoffinRefusalAlert breaches={refusal} />}
            {noAnswer && <DoffinNoAnswerAlert noAnswer={noAnswer} />}
          </Stack>
          <Flex justify="end" pt="6">
            <Button colorScheme="red" isLoading={isLoading} isDisabled={noAnswer === "whileSending"} onClick={send}>
              {t("End and announce on Doffin")}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
